import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Row, Col, Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQuery } from "@apollo/react-hooks";
import displayErrorToast from "../../components/utils/displayErrorToast";
import PaginationComponent from "../../components/PaginationComponent";
import SubHeaderComponent from "../../components/SubHeaderComponent";
import LabelComponent from "../../components/utils/getCompanyLabel";
import EditRow from "./EditRow";
import DisplayRow from "./DisplayRow";
import {
  MODULES_QUERY,
  CREATE_MODULE_MUTATION,
  UPDATE_MODULE_MUTATION,
  DELETE_MODULE_MUTATION
} from "./resources/gql";
import * as DefaultLabels from '../../assets/glossary.json';
const labels = DefaultLabels.default;

const ReviewModulesScreen = ({ togglePageLoad, user, ...props}) => {
  const { data, loading } = useQuery(MODULES_QUERY)
  const [modules, setModules] = useState([]);
  const [isCreatingNewItem, setIsCreatingNewItem] = useState(false);
  const [itemBeingEdited, setItemBeingEdited] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [firstIndex, setFirstIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState(9);

  const getLabelText = key => <LabelComponent val={key}/>

  const getText = val => {
    const companyId = localStorage.getItem("companyId");
    let lang = "EN";
    let langLabels = labels.find(lbl => lbl.lang === lang);
    let def = langLabels.glossary.find(label => label.key === val);

    if(user && user.companies && user.companies.length) {
      const company = user.companies.find(company => company.id === companyId);
      if(company) {
        lang = company.language;
        langLabels = labels.find(lbl => lbl.lang === lang);
        def = langLabels.glossary.find(label => label.key === val);
        if(!def) {
          lang = "EN";
          langLabels = labels.find(lbl => lbl.lang === lang);
          def = langLabels.glossary.find(label => label.key === val);
        }
      }
      if(company && company.companyGlossaries) {
        const custom = company.companyGlossaries.find(label => label.key === val);

        if(custom) return custom.value
      }
    }

    if(!def) {
      console.log(val)
    }
    return def.value
  }

  const reset = () => {
    setIsCreatingNewItem(false);
    setItemBeingEdited("");
    props.toggleDirtyState()
  };

  const onError = (error) => {
    displayErrorToast(error);
    reset();
  };

  useEffect(() => {
    if(data && data.reviewModules) {
      setModules(data.reviewModules)
      setTotalPages((data.reviewModules.length ? Math.ceil(data.reviewModules.length / 10) : 1))
    }
    togglePageLoad(loading)
  }, [data, loading, setModules, togglePageLoad])

  const [createModule] = useMutation(CREATE_MODULE_MUTATION, {
    onError,
    onCompleted: ({ createReviewModule }) => {
      setModules([createReviewModule, ...modules]);
      reset();
    },
  });

  const [updateModule] = useMutation(UPDATE_MODULE_MUTATION, {
    onError,
    onCompleted: ({ updateReviewModule }) => {
      const nextModules = modules.map((e) =>
        e.id === updateReviewModule.id ? updateReviewModule : e
      );
      setModules(nextModules);
      reset();
    },
  });

  const [deleteModule] = useMutation(DELETE_MODULE_MUTATION, {
    onError,
    onCompleted: ({ deleteReviewModule }) => {
      setModules(
        modules.filter((e) => e.id !== deleteReviewModule.id)
      );
      reset();
    },
  });

  const onOpenCreate = () => {
    setIsCreatingNewItem(true);
    props.toggleDirtyState()
    setItemBeingEdited("");
  };

  const onOpenEdit = (id) => {
    setIsCreatingNewItem(false);
    props.toggleDirtyState()
    setItemBeingEdited(id);
  };

  const onDelete = (id) => {
    deleteModule({ variables: { id } });
  };

  const onSave = ({ id, name }) => {
    if (id) return updateModule({ variables: { id, name } });
    createModule({ variables: { name } });
  };

  function setPagination (page) {
    setFirstIndex((parseInt(page) * 10) - 10)
    setLastIndex((parseInt(page) * 10) - 1)
    setPage(page)
  }

  return (
    <>
      <SubHeaderComponent>
        <Row className="justify-content-center">
          <Col className="text-right" xs={12}>
            <Button onClick={onOpenCreate} className={"btn-nrml" + (isCreatingNewItem || itemBeingEdited !== "" ? " disabled" : "")}>
              {getLabelText("add")}
              <FontAwesomeIcon className="ml-2" icon={faPlus} />
            </Button>
          </Col>
        </Row>
      </SubHeaderComponent>
      <div className="grey-header-space container-left">
        <Row className="justify-content-center">
          <Col xl={12}>
            <form>
              <Table striped bordered hover className="table-heading-background">
                <thead>
                <tr>
                  <th width="25%" className="text-center">{getLabelText("module_description")}</th>
                  <th width="15%" className="text-center"></th>
                </tr>
                </thead>
                {modules && (
                    <tbody>
                    {isCreatingNewItem ? (
                        <EditRow onSave={onSave} onCancel={reset} />
                    ) : null}
                    {modules.map(({ name, id, canDelete }, i) =>
                        i >= firstIndex && i <= lastIndex ? (
                            id === itemBeingEdited ? (
                                <EditRow
                                    key={id}
                                    name={name}
                                    id={id}
                                    onSave={onSave}
                                    onCancel={reset}
                                />
                            ) : (
                                <DisplayRow
                                    onOpenEdit={onOpenEdit}
                                    onDelete={onDelete}
                                    getText={getText}
                                    canDelete={canDelete}
                                    key={id}
                                    name={name}
                                    id={id}
                                    disabled={isCreatingNewItem || itemBeingEdited !== ""}
                                />
                            )
                        ) : null
                    )}
                    </tbody>
                )}
              </Table>
              {totalPages > 1 && (
                  <PaginationComponent
                      active={page}
                      disabled={false}
                      setPage={setPagination}
                      total={totalPages}
                  />
              )}
            </form>
          </Col>
        </Row>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.userReducer.user
})

const mapDispatchToProps = {

};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewModulesScreen);
